import { date } from 'quasar'
import Model, { IsoDateTime } from 'src/models/model'
import { useMinimumDeliveryTime } from 'src/modules/checkout/composeables/order/MinimumDeliveryTime'
import { computed, toValue } from 'vue'

const { addToDate } = date

const DEFAULT_OPTIONS = Object.freeze({
  showing: null
})

export function useSuggestedDeliveryTimes (orderItems = [], options = DEFAULT_OPTIONS) {
  const currentSite = Model.configuration?.currentSite
  const { computeMinimumDeliveryTime } = useMinimumDeliveryTime()

  function computeSuggestedDeliveryTimes (orderItems, { showing } = DEFAULT_OPTIONS) {
    const times = []
    const orderItemsValue = toValue(orderItems)

    if (_.isNil(showing)) {
      return times
    }

    const timeNow = new Date()
    const showingTime = showing?.time
    // First option: ASAP (minimum delivery time)
    const asapTime = computeMinimumDeliveryTime(orderItemsValue, { showing })
    const asapMinutes = date.getDateDiff(asapTime, timeNow, 'minutes')
    const asapTimeLabel = asapTime.inTimeZone(currentSite.timeZone).format('h:mm A')
    times.push({
      label: this.$t('checkout.delivery.times.asap'),
      timeLabel: asapTimeLabel,
      time: asapTime,
      minutes: asapMinutes
    })

    // Second option: Beginning (showing start time)
    const minutesUntilShowing = date.getDateDiff(showingTime, timeNow, 'minutes')
    const timeDifference = minutesUntilShowing - asapMinutes

    // Only add beginning time if there's at least 15 minutes difference from ASAP
    if (timeDifference >= 15) {
      const beginningTimeLabel = showingTime.inTimeZone(currentSite.timeZone).format('h:mm A')
      times.push({
        label: this.$t('checkout.delivery.times.early'),
        timeLabel: beginningTimeLabel,
        time: showingTime,
        minutes: minutesUntilShowing
      })
    }

    // Calculate middle and late delivery options
    const showingStartTime = showingTime
    const showingEndTime = showing?.endTime
    const lateDeliveryTime = new IsoDateTime(addToDate(showingEndTime, { minutes: -30 })) // 30 minutes before end
    const middleDeliveryTime = new IsoDateTime(addToDate(showingStartTime, {
      minutes: Math.floor(date.getDateDiff(lateDeliveryTime, showingStartTime, 'minutes') / 2)
    })) // Halfway between start and late time

    // Add middle option (if there's enough time)
    const minutesBetweenStartAndMiddle = date.getDateDiff(middleDeliveryTime, showingStartTime, 'minutes')
    if (minutesBetweenStartAndMiddle >= 15) { // At least 15 minutes between start and middle
      const totalMinutes = date.getDateDiff(middleDeliveryTime, timeNow, 'minutes')
      const middleTimeLabel = middleDeliveryTime.inTimeZone(currentSite.timeZone).format('h:mm A')

      times.push({
        label: this.$t('checkout.delivery.times.middle'),
        timeLabel: middleTimeLabel,
        time: middleDeliveryTime,
        minutes: totalMinutes
      })
    }

    // Add late option (if there's enough time)
    const minutesBetweenStartAndLate = date.getDateDiff(lateDeliveryTime, showingStartTime, 'minutes')
    if (minutesBetweenStartAndLate >= 30) { // At least 30 minutes between start and late
      const totalMinutes = date.getDateDiff(lateDeliveryTime, timeNow, 'minutes')
      const lateTimeLabel = lateDeliveryTime.inTimeZone(currentSite.timeZone).format('h:mm A')

      times.push({
        label: this.$t('checkout.delivery.times.late'),
        timeLabel: lateTimeLabel,
        time: lateDeliveryTime,
        minutes: totalMinutes
      })
    }

    return times
  }

  const suggestedDeliveryTimes = computed(() => computeSuggestedDeliveryTimes(computed(() => orderItems), options))
  return {
    computeSuggestedDeliveryTimes,
    suggestedDeliveryTimes
  }
}
